import { useEffect } from 'react';
import { captureException } from '@sentry/react';

import { logInDev } from '@ocx/utils';
import { getDevicePlatform } from '@ocx/utils-device';
import { useConfiguration } from '@ocx/data-configuration';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useRudderStack, PushNotificationStoredEventParams } from '@ocx/data-analytics';

import { PROCESSED_EVENT_RETENTION_MS } from './push-notification-events.constants';
import { getStoredPushNotificationEvents, saveStoredPushNotificationEvents } from './push-notification-events.storage';
import { PushNotificationEvent } from './push-notification-events.types';

const isExpired = (event: PushNotificationEvent, now: number): boolean => {
  // Use api_processed_at as the primary anchor; fall back to analytics_processed_at.
  // An event is only considered expired once at least one processor has stamped it.
  const processedAt = event.api_processed_at ?? event.analytics_processed_at;
  return processedAt !== undefined && now - processedAt > PROCESSED_EVENT_RETENTION_MS;
};

const needsProcessing = (
  event: PushNotificationEvent,
  analyticsReportingEnabled: boolean,
  apiReportingEnabled: boolean,
): boolean =>
  (analyticsReportingEnabled && event.analytics_processed_at === undefined) ||
  (apiReportingEnabled && event.api_processed_at === undefined);

const mergeProcessedEvents = (
  events: PushNotificationEvent[],
  processed: PushNotificationEvent[],
): PushNotificationEvent[] => {
  const processedById = new Map(processed.map((e) => [e.id, e]));
  return events.map((e) => processedById.get(e.id) ?? e);
};

const processAnalyticsEvents = async (
  events: PushNotificationEvent[],
  triggerEvent: ReturnType<typeof useRudderStack>['triggerEvent'],
  now: number,
): Promise<PushNotificationEvent[]> => {
  // RudderStack JS SDK queues track() calls internally and flushes them in
  // batched HTTP requests — no manual batching needed here.
  events.forEach((event) => {
    const params: PushNotificationStoredEventParams = {
      push_id: event.push_id,
      event_type: event.event_type,
      occurred_at: event.occurred_at,
    };

    if (event.event_type === 'notification_received') return triggerEvent('push_notification_received', params);
    if (event.event_type === 'notification_tapped') return triggerEvent('push_notification_tapped', params);
    if (event.event_type === 'notification_blocked') return triggerEvent('push_notification_blocked', params);
    if (event.event_type === 'notification_dismissed') return triggerEvent('push_notification_dismissed', params);
    if (event.event_type === 'notification_link_opened') return triggerEvent('push_notification_link_opened', params);
  });
  return events.map((event) => ({ ...event, analytics_processed_at: now }));
};

const processApiEvents = async (events: PushNotificationEvent[], now: number): Promise<PushNotificationEvent[]> => {
  // TODO(OCST-27458 follow-up): log events to API
  return events.map((event) => ({ ...event, api_processed_at: now }));
};

export const useProcessStoredPushNotificationEvents = () => {
  const { enabled, analyticsReportingEnabled, apiReportingEnabled } = useConfiguration('pushNotificationReporting');
  const { triggerEvent } = useRudderStack();

  useEffect(() => {
    if (getDevicePlatform() === 'web') return;
    if (!enabled) return;
    if (!analyticsReportingEnabled && !apiReportingEnabled) return;

    const processAndPersistEvents = async () => {
      try {
        const storedEvents = await getStoredPushNotificationEvents();
        if (storedEvents.length === 0) return;

        const now = Date.now();
        const activeEvents = storedEvents.filter((event) => !isExpired(event, now));

        const fullyProcessed = activeEvents.filter(
          (event) => !needsProcessing(event, analyticsReportingEnabled, apiReportingEnabled),
        );
        let eventsToSave = activeEvents.filter((event) =>
          needsProcessing(event, analyticsReportingEnabled, apiReportingEnabled),
        );

        if (analyticsReportingEnabled) {
          const toProcess = eventsToSave.filter((event) => event.analytics_processed_at === undefined);
          const processed = await processAnalyticsEvents(toProcess, triggerEvent, now);
          eventsToSave = mergeProcessedEvents(eventsToSave, processed);
        }
        if (apiReportingEnabled) {
          const toProcess = eventsToSave.filter((event) => event.api_processed_at === undefined);
          const processed = await processApiEvents(toProcess, now);
          eventsToSave = mergeProcessedEvents(eventsToSave, processed);
        }

        const sortedEvents = [...fullyProcessed, ...eventsToSave].sort((a, b) => a.occurred_at - b.occurred_at);
        await saveStoredPushNotificationEvents(sortedEvents);
      } catch (error) {
        captureException(error, { tags: { source: 'useProcessStoredPushNotificationEvents' } });
        logInDev(error);
      }
    };

    void processAndPersistEvents();
  }, [enabled, analyticsReportingEnabled, apiReportingEnabled, triggerEvent]);
};
